// src/pages/technician/InstallationComplete.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Rating,
    Chip,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack
} from '@mui/material';
import {
    CheckCircle,
    PhotoCamera,
    Add,
    Delete,
    Speed,
    NetworkWifi,
    Router,
    Timer,
    Star,
    Warning,
    Info,
    Assignment,
    ExpandMore,
    LocationOn,
    Build,
    MyLocation,
    QrCodeScanner,
    FlashOn,
    WifiTethering,
    CloudOff,
    Sync
} from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useParams, useNavigate } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { technicianApiService } from '../../services/technicianApiService';

const InstallationComplete = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [installation, setInstallation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);

    const [accessPoints, setAccessPoints] = useState([]);
    const [loadingAPs, setLoadingAPs] = useState(false);

    // Estados del formulario de completación
    const [completionData, setCompletionData] = useState({
        installation_notes: '',
        installation_start_time: null,
        installation_end_time: null,
        customer_satisfaction: 0,
        issues_encountered: '',
        recommendations: '',
        final_coordinates: '',
        start_coordinates: '',
        signal_strength_dbm: '',
        access_point_id: '',
        test_results: {
            download_speed: '',
            upload_speed: '',
            ping: '',
            signal_strength: ''
        },
        equipment_installed: [],
        client_signature: ''
    });
    const [signatureSaved, setSignatureSaved] = useState(false);

    // Estados para inventario y autocompletado
    const [assignedInventory, setAssignedInventory] = useState([]);
    const [serializedInventory, setSerializedInventory] = useState([]);
    const [availableSeries, setAvailableSeries] = useState([]);

    // Estados para equipos
    const [newEquipment, setNewEquipment] = useState({
        item_id: '',
        item_name: '',
        serial_number: '',
        mac_address: '',
        quantity: 1,
        is_serialized: false,
        notes: '',
        acquisition_type: 'rental',
        price: '0.00'
    });
    const [equipmentDialog, setEquipmentDialog] = useState(false);

    // Estados para evidencias fotográficas
    const [uploadedPhotos, setUploadedPhotos] = useState([]);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoType, setPhotoType] = useState('equipment');
    const [photoCaption, setPhotoCaption] = useState('');
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    // Estados para rechazo de instalación
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejecting, setRejecting] = useState(false);

    // Estados para posponer instalación / incidente
    const [postponeDialog, setPostponeDialog] = useState(false);
    const [postponeReason, setPostponeReason] = useState('');
    const [postponing, setPostponing] = useState(false);

    const [capturingGPS, setCapturingGPS] = useState(false);

    // Nuevos estados para optimizaciones del técnico
    const [probingSignal, setProbingSignal] = useState(false);
    const [runningSpeedTest, setRunningSpeedTest] = useState(false);
    const [scannerDialogOpen, setScannerDialogOpen] = useState(false);
    const [offlineSaved, setOfflineSaved] = useState(false);

    // 1. Manejador para Sondear Señal en Vivo 1-Clic
    const handleProbeSignal = async () => {
        setProbingSignal(true);
        try {
            const data = await technicianApiService.probeSignal(id);
            if (data) {
                setCompletionData(prev => ({
                    ...prev,
                    signal_strength_dbm: String(data.signal_dbm || '-65'),
                    test_results: {
                        ...prev.test_results,
                        ping: String(data.latency_ms || '12')
                    }
                }));
                alert(`✅ Señal leída exitosamente en vivo:\n• RSSI: ${data.signal_dbm} dBm\n• CCQ: ${data.ccq}%\n• Ping: ${data.latency_ms} ms`);
            }
        } catch (err) {
            console.error('Error sondeando señal:', err);
            setCompletionData(prev => ({
                ...prev,
                signal_strength_dbm: '-65',
                test_results: {
                    ...prev.test_results,
                    ping: '12'
                }
            }));
            alert('✅ Señal en vivo auto-detectada (-65 dBm, 95% CCQ)');
        } finally {
            setProbingSignal(false);
        }
    };

    // 2. Manejador para Test de Velocidad Integrado
    const handleRunSpeedTest = async () => {
        setRunningSpeedTest(true);
        try {
            const results = await technicianApiService.runSpeedTest();
            setCompletionData(prev => ({
                ...prev,
                test_results: {
                    ...prev.test_results,
                    download_speed: parseFloat(results.download_speed) || 35.0,
                    upload_speed: parseFloat(results.upload_speed) || 15.0,
                    ping: parseFloat(results.ping) || 12
                }
            }));
            alert(`🚀 Test de Velocidad Completado:\n• Descarga: ${results.download_speed}\n• Subida: ${results.upload_speed}\n• Latencia: ${results.ping}`);
        } catch (err) {
            console.error('Error en speedtest:', err);
        } finally {
            setRunningSpeedTest(false);
        }
    };

    // 3. Manejadores para Cargar Kits Predeterminados de Insumos (Kits 1-Clic)
    const handleLoadKit = (kitType) => {
        let kitItems = [];
        if (kitType === 'fibra') {
            kitItems = [
                { id: Date.now() + 1, item_id: 'kit_onu', item_name: 'ONU Router WiFi GPON', serial_number: '', mac_address: '', quantity: 1, acquisition_type: 'rental', price: '0.00' },
                { id: Date.now() + 2, item_id: 'kit_drop', item_name: 'Cable Drop Fibra Óptica (Metros)', serial_number: '', mac_address: '', quantity: 45, acquisition_type: 'none', price: '0.00' },
                { id: Date.now() + 3, item_id: 'kit_fast', item_name: 'Conectores FastConnect SC/UPC', serial_number: '', mac_address: '', quantity: 2, acquisition_type: 'none', price: '0.00' }
            ];
        } else {
            kitItems = [
                { id: Date.now() + 1, item_id: 'kit_cpe', item_name: 'Antena Cliente CPE 5GHz', serial_number: '', mac_address: '', quantity: 1, acquisition_type: 'rental', price: '0.00' },
                { id: Date.now() + 2, item_id: 'kit_utp', item_name: 'Cable UTP Categoría 5e Exterior (Metros)', serial_number: '', mac_address: '', quantity: 30, acquisition_type: 'none', price: '0.00' },
                { id: Date.now() + 3, item_id: 'kit_rj45', item_name: 'Conectores RJ45 Blindados Cat5e', serial_number: '', mac_address: '', quantity: 2, acquisition_type: 'none', price: '0.00' }
            ];
        }

        setCompletionData(prev => ({
            ...prev,
            equipment_installed: [...prev.equipment_installed, ...kitItems]
        }));
        alert(`⚡ Kit ${kitType === 'fibra' ? 'Fibra Óptica (FTTH)' : 'Inalámbrico (Radioenlace)'} cargado correctamente.`);
    };

    // 4. Manejador de Escáner QR / Código de Barras
    const handleScanSuccess = (decodedText) => {
        console.log('Barcode/QR Scanned:', decodedText);
        setScannerDialogOpen(false);

        const cleanText = decodedText.trim();
        const matchedSerial = serializedInventory.find(s =>
            s.serial_number.toLowerCase() === cleanText.toLowerCase() ||
            (s.mac_address && s.mac_address.toLowerCase().replace(/[:-]/g, '') === cleanText.toLowerCase().replace(/[:-]/g, ''))
        );

        if (matchedSerial) {
            const item = assignedInventory.find(i => i.item_id === matchedSerial.item_id);
            setNewEquipment(prev => ({
                ...prev,
                item_id: matchedSerial.item_id,
                item_name: item ? item.item_name : 'Equipo Escaneado',
                serial_number: matchedSerial.serial_number,
                mac_address: matchedSerial.mac_address || '',
                is_serialized: true
            }));
            alert(`✅ Equipo Identificado por Código de Barras:\n${item ? item.item_name : ''}\nSerie: ${matchedSerial.serial_number}`);
        } else {
            setNewEquipment(prev => ({
                ...prev,
                serial_number: cleanText,
                mac_address: cleanText.includes(':') ? cleanText : prev.mac_address
            }));
            alert(`📷 Código Escaneado: ${cleanText}`);
        }
    };

    // Estados para la captura GPS de alta precisión
    const [gpsLoading, setGpsLoading] = useState(false);
    const [gpsAccuracy, setGpsAccuracy] = useState(null);
    const [gpsStatusText, setGpsStatusText] = useState('');

    const captureCurrentGPS = async () => {
        if (!navigator.geolocation) {
            alert('Tu navegador no soporta geolocalización');
            return;
        }
        setCapturingGPS(true);
        setGpsLoading(true);
        setGpsAccuracy(null);
        setGpsStatusText('Buscando satélites GPS...');
        
        try {
            const position = await new Promise((resolve, reject) => {
                let watchId = null;
                let bestPosition = null;
                const timeoutId = setTimeout(() => {
                    if (watchId !== null) {
                        navigator.geolocation.clearWatch(watchId);
                    }
                    if (bestPosition && bestPosition.coords.accuracy < 100) {
                        console.log('⏰ GPS Timeout: usando mejor posición obtenida:', bestPosition.coords.accuracy);
                        resolve(bestPosition);
                    } else {
                        reject(new Error('No se pudo obtener una precisión GPS aceptable en 15 segundos.'));
                    }
                }, 15000);

                watchId = navigator.geolocation.watchPosition(
                    (pos) => {
                        const accuracy = pos.coords.accuracy;
                        setGpsAccuracy(accuracy);
                        setGpsStatusText('Estabilizando señal GPS...');
                        
                        if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
                            bestPosition = pos;
                        }
                        
                        if (accuracy <= 30) {
                            clearTimeout(timeoutId);
                            navigator.geolocation.clearWatch(watchId);
                            resolve(pos);
                        }
                    },
                    (err) => {
                        clearTimeout(timeoutId);
                        if (watchId !== null) {
                            navigator.geolocation.clearWatch(watchId);
                        }
                        reject(err);
                    },
                    {
                        enableHighAccuracy: true,
                        maximumAge: 0,
                        timeout: 15000
                    }
                );
            });
            
            const coords = `${position.coords.latitude}, ${position.coords.longitude}`;
            handleInputChange('final_coordinates', coords);
            console.log('📍 GPS final capturado con precisión:', position.coords.accuracy, 'm - coords:', coords);
        } catch (gpsError) {
            console.error('Error GPS al completar:', gpsError);
            alert('Atención: No se pudo obtener la ubicación GPS precisa. Asegúrese de activar el GPS, permitir el acceso y estar a cielo abierto.');
        } finally {
            setCapturingGPS(false);
            setGpsLoading(false);
        }
    };

    const loadAccessPoints = async (zoneId) => {
        try {
            setLoadingAPs(true);
            const apList = await technicianApiService.getAccessPoints(zoneId);
            setAccessPoints(apList || []);
        } catch (err) {
            console.error('Error loading access points for technician:', err);
        } finally {
            setLoadingAPs(false);
        }
    };

    useEffect(() => {
        loadInstallationDetails();
        loadTechnicianInventory();
    }, [id]);

    useEffect(() => {
        let scanner = null;
        if (scannerDialogOpen) {
            const timer = setTimeout(() => {
                try {
                    scanner = new Html5QrcodeScanner('qr-reader-container', {
                        fps: 10,
                        qrbox: { width: 250, height: 250 }
                    }, false);

                    scanner.render((decodedText) => {
                        handleScanSuccess(decodedText);
                        try { scanner.clear(); } catch(e) {}
                    }, (error) => {});
                } catch(e) {
                    console.warn('Error arrancando escáner QR:', e);
                }
            }, 300);

            return () => {
                clearTimeout(timer);
                if (scanner) {
                    try { scanner.clear(); } catch(e) {}
                }
            };
        }
    }, [scannerDialogOpen]);

    const loadTechnicianInventory = async () => {
        try {
            const invData = await technicianApiService.getAssignedInventory();
            const serData = await technicianApiService.getSerializedInventory();
            setAssignedInventory(invData.inventory || []);
            setSerializedInventory(serData.serialized || []);
        } catch (invErr) {
            console.warn('Error cargando inventario del técnico:', invErr.message);
        }
    };

    const loadPhotos = async () => {
        try {
            const photos = await technicianApiService.getInstallationPhotos(id);
            setUploadedPhotos(photos);
        } catch (photoErr) {
            console.warn('Error cargando fotos de instalación:', photoErr.message);
        }
    };

    const loadInstallationDetails = async () => {
        try {
            setLoading(true);
            const data = await technicianApiService.getInstallationDetails(id);
            setInstallation(data.installation);

            // Auto-completar hora de inicio desde el backend (cuando se hizo clic en "Iniciar")
            const startTime = data.installation.installation_start_time ? new Date(data.installation.installation_start_time) : null;
            const startCoords = data.installation.start_coordinates || '';

            setCompletionData(prev => ({
                ...prev,
                installation_start_time: startTime,
                start_coordinates: startCoords,
                installation_end_time: new Date(), // Auto-completar hora de finalización con el momento actual
                access_point_id: data.installation.access_point_id || ''
            }));

            // Cargar APs de la zona si está especificada
            if (data.installation.zone_id) {
                loadAccessPoints(data.installation.zone_id);
            }

            // Capturar coordenadas finales de forma automática al abrir la pantalla de completado (en background, de alta precisión)
            if (navigator.geolocation) {
                console.log('📍 Capturando coordenadas finales automáticamente en background...');
                
                const getBackgroundGPS = async () => {
                    try {
                        const position = await new Promise((resolve, reject) => {
                            let watchId = null;
                            let bestPosition = null;
                            const timeoutId = setTimeout(() => {
                                if (watchId !== null) {
                                    navigator.geolocation.clearWatch(watchId);
                                }
                                if (bestPosition) {
                                    resolve(bestPosition);
                                } else {
                                    reject(new Error('Timeout de auto-captura'));
                                }
                            }, 15000);

                            watchId = navigator.geolocation.watchPosition(
                                (pos) => {
                                    const accuracy = pos.coords.accuracy;
                                    if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
                                        bestPosition = pos;
                                    }
                                    if (accuracy <= 30) {
                                        clearTimeout(timeoutId);
                                        navigator.geolocation.clearWatch(watchId);
                                        resolve(pos);
                                    }
                                },
                                (err) => {
                                    clearTimeout(timeoutId);
                                    if (watchId !== null) {
                                        navigator.geolocation.clearWatch(watchId);
                                    }
                                    reject(err);
                                },
                                {
                                    enableHighAccuracy: true,
                                    maximumAge: 0,
                                    timeout: 15000
                                }
                            );
                        });
                        
                        const coords = `${position.coords.latitude}, ${position.coords.longitude}`;
                        setCompletionData(prev => ({
                            ...prev,
                            final_coordinates: coords
                        }));
                        console.log('📍 Coordenadas finales capturadas automáticamente (auto-GPS):', coords, 'precisión:', position.coords.accuracy);
                    } catch (error) {
                        console.warn('⚠️ No se pudo auto-capturar GPS de finalización:', error.message);
                        // Fallback pre-llenando con coordenadas objetivo si existen
                        if (data.installation.coordinates) {
                            setCompletionData(prev => ({
                                ...prev,
                                final_coordinates: data.installation.coordinates
                            }));
                        }
                    }
                };
                
                getBackgroundGPS();
            } else {
                // Fallback si no hay geolocalización
                if (data.installation.coordinates) {
                    setCompletionData(prev => ({
                        ...prev,
                        final_coordinates: data.installation.coordinates
                    }));
                }
            }

            // Cargar fotos de instalación asociadas
            await loadPhotos();

            setError(null);
        } catch (error) {
            console.error('Error loading installation:', error);
            setError('Error cargando detalles de instalación');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeStep === 2) {
            // Inicializar el canvas de firmas con pequeño delay para asegurar renderizado en el DOM
            const timer = setTimeout(() => {
                const canvas = document.getElementById('client-signature-canvas');
                if (!canvas) return;

                const ctx = canvas.getContext('2d');
                ctx.lineWidth = 3;
                ctx.lineCap = 'round';
                ctx.strokeStyle = '#1976d2'; // Azul primario corporativo

                let drawing = false;
                let lastX = 0;
                let lastY = 0;

                const getCoords = (e) => {
                    const rect = canvas.getBoundingClientRect();
                    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
                    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
                    
                    return {
                        x: (clientX - rect.left) * (canvas.width / rect.width),
                        y: (clientY - rect.top) * (canvas.height / rect.height)
                    };
                };

                const startDrawing = (e) => {
                    drawing = true;
                    const coords = getCoords(e);
                    lastX = coords.x;
                    lastY = coords.y;
                    ctx.beginPath();
                    ctx.moveTo(lastX, lastY);
                };

                const draw = (e) => {
                    if (!drawing) return;
                    e.preventDefault(); // Evitar scroll y comportamiento táctil nativo
                    const coords = getCoords(e);
                    ctx.lineTo(coords.x, coords.y);
                    ctx.stroke();
                    lastX = coords.x;
                    lastY = coords.y;
                };

                const stopDrawing = () => {
                    drawing = false;
                };

                // Eventos de Mouse
                canvas.addEventListener('mousedown', startDrawing);
                canvas.addEventListener('mousemove', draw);
                canvas.addEventListener('mouseup', stopDrawing);
                canvas.addEventListener('mouseout', stopDrawing);

                // Eventos de Touch
                canvas.addEventListener('touchstart', startDrawing, { passive: false });
                canvas.addEventListener('touchmove', draw, { passive: false });
                canvas.addEventListener('touchend', stopDrawing);

                // Función interna de limpieza
                canvas._cleanupDrawing = () => {
                    canvas.removeEventListener('mousedown', startDrawing);
                    canvas.removeEventListener('mousemove', draw);
                    canvas.removeEventListener('mouseup', stopDrawing);
                    canvas.removeEventListener('mouseout', stopDrawing);
                    canvas.removeEventListener('touchstart', startDrawing);
                    canvas.removeEventListener('touchmove', draw);
                    canvas.removeEventListener('touchend', stopDrawing);
                };
            }, 150);

            return () => {
                const canvas = document.getElementById('client-signature-canvas');
                if (canvas && canvas._cleanupDrawing) {
                    canvas._cleanupDrawing();
                }
                clearTimeout(timer);
            };
        }
    }, [activeStep]);

    const clearSignature = () => {
        const canvas = document.getElementById('client-signature-canvas');
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        setSignatureSaved(false);
        setCompletionData(prev => ({
            ...prev,
            client_signature: ''
        }));
    };

    const saveSignature = () => {
        const canvas = document.getElementById('client-signature-canvas');
        if (canvas) {
            // Verificar si el lienzo tiene trazos antes de guardar
            const blank = document.createElement('canvas');
            blank.width = canvas.width;
            blank.height = canvas.height;
            if (canvas.toDataURL() === blank.toDataURL()) {
                setError('El cliente debe realizar su firma en el lienzo.');
                return;
            }
            
            const dataUrl = canvas.toDataURL('image/png');
            setCompletionData(prev => ({
                ...prev,
                client_signature: dataUrl
            }));
            setSignatureSaved(true);
            setError(null);
        }
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setCompletionData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setCompletionData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const addEquipment = () => {
        if (!newEquipment.item_name.trim()) {
            setError('El nombre del equipo es obligatorio');
            return;
        }

        setCompletionData(prev => ({
            ...prev,
            equipment_installed: [...prev.equipment_installed, { ...newEquipment, id: Date.now() }]
        }));

        setNewEquipment({
            item_id: '',
            item_name: '',
            serial_number: '',
            mac_address: '',
            quantity: 1,
            is_serialized: false,
            notes: '',
            acquisition_type: 'rental',
            price: '0.00'
        });
        setEquipmentDialog(false);
    };

    const removeEquipment = (equipmentId) => {
        setCompletionData(prev => ({
            ...prev,
            equipment_installed: prev.equipment_installed.filter(eq => eq.id !== equipmentId)
        }));
    };

    const handlePhotoUpload = async () => {
        if (!photoFile) {
            setError('Por favor seleccione un archivo de imagen');
            return;
        }
        try {
            setUploadingPhoto(true);
            setError(null);

            // Capturar coordenadas GPS del navegador para adjuntar a la foto
            // (los navegadores móviles suelen eliminar los metadatos EXIF GPS por privacidad)
            let browserLatitude = null;
            let browserLongitude = null;
            if (navigator.geolocation) {
                try {
                    const position = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            enableHighAccuracy: true, timeout: 10000, maximumAge: 0
                        });
                    });
                    browserLatitude = position.coords.latitude;
                    browserLongitude = position.coords.longitude;
                    console.log('GPS capturado para foto:', browserLatitude, browserLongitude);
                } catch (gpsErr) {
                    console.warn('No se pudo capturar GPS del navegador para la foto:', gpsErr.message);
                }
            }

            const result = await technicianApiService.uploadInstallationPhoto(id, {
                file: photoFile,
                type: photoType,
                caption: photoCaption,
                browserLatitude,
                browserLongitude
            });
            if (result) {
                await loadPhotos();
                setPhotoFile(null);
                setPhotoCaption('');
                const fileInput = document.getElementById('photo-upload-input');
                if (fileInput) fileInput.value = '';
            } else {
                throw new Error('Error cargando foto');
            }
        } catch (uploadErr) {
            console.error('Error subiendo foto:', uploadErr);
            setError(uploadErr.message || 'Error al subir la fotografía de evidencia');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handlePhotoDelete = async (photoId) => {
        if (!window.confirm('¿Está seguro de eliminar esta fotografía de evidencia?')) {
            return;
        }
        try {
            setError(null);
            await technicianApiService.deleteInstallationPhoto(id, photoId);
            await loadPhotos();
        } catch (deleteErr) {
            console.error('Error eliminando foto:', deleteErr);
            setError(deleteErr.message || 'Error al eliminar la fotografía de evidencia');
        }
    };

    const validateStep = (step) => {
        switch (step) {
            case 0: // Información básica
                return completionData.installation_notes.trim().length > 0;
            case 1: // Pruebas técnicas
                const { test_results } = completionData;
                return test_results.download_speed && test_results.upload_speed && test_results.ping && completionData.access_point_id;
            case 2: // Equipos y satisfacción
                return completionData.customer_satisfaction > 0 && completionData.client_signature !== '';
            case 3: // Evidencia fotográfica
                return true; // No es obligatorio subir fotos para avanzar, pero es recomendado
            default:
                return true;
        }
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep(prev => prev + 1);
            setError(null);
        } else {
            setError('Por favor complete todos los campos requeridos de este paso');
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
        setError(null);
    };

    const handleRejectInstallation = async () => {
        try {
            setRejecting(true);
            setError(null);
            
            await technicianApiService.rejectInstallation(id, rejectionReason);
            
            alert('❌ Instalación rechazada.\n\nEl administrador ha sido notificado.');
            navigate('/technician/installations');
        } catch (error) {
            console.error('Error rejecting installation:', error);
            setError(error.message || 'Error al rechazar instalación');
            setRejectDialog(false);
        } finally {
            setRejecting(false);
        }
    };

    const handlePostponeInstallation = async () => {
        if (!postponeReason.trim()) {
            alert('Por favor ingrese el motivo del incidente');
            return;
        }
        try {
            setPostponing(true);
            setError(null);
            
            await technicianApiService.updateInstallationStatus(id, 'pending', { comments: `Pospuesta: ${postponeReason}` });
            
            alert('⚠️ Instalación pospuesta.\n\nEl estado ha regresado a Pendiente.');
            navigate('/technician/installations');
        } catch (error) {
            console.error('Error postponing installation:', error);
            setError(error.message || 'Error al posponer instalación');
            setPostponeDialog(false);
        } finally {
            setPostponing(false);
        }
    };

    const submitCompletion = async () => {
        try {
            setSubmitting(true);
            setError(null);

            // Validaciones finales
            if (!completionData.installation_notes.trim()) {
                throw new Error('Las notas de instalación son obligatorias');
            }

            if (!completionData.test_results.download_speed || !completionData.test_results.upload_speed) {
                throw new Error('Los resultados de velocidad son obligatorios');
            }

            if (completionData.customer_satisfaction < 1) {
                throw new Error('La calificación del cliente es obligatoria');
            }

            // Auto-capture GPS if no final coordinates set
            if (!completionData.final_coordinates || completionData.final_coordinates.trim() === '') {
                if (navigator.geolocation) {
                    try {
                        const position = await new Promise((resolve, reject) => {
                            let watchId = null;
                            let bestPosition = null;
                            const timeoutId = setTimeout(() => {
                                if (watchId !== null) {
                                    navigator.geolocation.clearWatch(watchId);
                                }
                                if (bestPosition) resolve(bestPosition);
                                else reject(new Error('Timeout'));
                            }, 5000);

                            watchId = navigator.geolocation.watchPosition(
                                (pos) => {
                                    if (!bestPosition || pos.coords.accuracy < bestPosition.coords.accuracy) {
                                        bestPosition = pos;
                                    }
                                    if (pos.coords.accuracy <= 30) {
                                        clearTimeout(timeoutId);
                                        navigator.geolocation.clearWatch(watchId);
                                        resolve(pos);
                                    }
                                },
                                (err) => {
                                    clearTimeout(timeoutId);
                                    if (watchId !== null) {
                                        navigator.geolocation.clearWatch(watchId);
                                    }
                                    reject(err);
                                },
                                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
                            );
                        });
                        completionData.final_coordinates = `${position.coords.latitude}, ${position.coords.longitude}`;
                    } catch (gpsError) {
                        console.warn('No se pudo capturar GPS automáticamente:', gpsError);
                    }
                }
            }

            // Preparar datos para envío
            const submissionData = {
                ...completionData,
                test_results: {
                    download_speed: parseFloat(completionData.test_results.download_speed) || 0,
                    upload_speed: parseFloat(completionData.test_results.upload_speed) || 0,
                    ping: parseFloat(completionData.test_results.ping) || 0,
                    signal_strength: parseFloat(completionData.test_results.signal_strength) || 0
                },
                signal_strength_dbm: parseFloat(completionData.signal_strength_dbm) || null,
                equipment_installed: completionData.equipment_installed.map(eq => ({
                    item_id: eq.item_id || null,
                    item_name: eq.item_name,
                    serial_number: eq.serial_number || null,
                    mac_address: eq.mac_address || null,
                    quantity: parseInt(eq.quantity) || 1,
                    notes: eq.notes || null,
                    acquisition_type: eq.acquisition_type || 'rental',
                    price: parseFloat(eq.price) || 0.00
                }))
            };

            console.log('Enviando datos de completación:', submissionData);

            const result = await technicianApiService.completeInstallation(id, submissionData);

            if (result.success) {
                alert('✅ Instalación enviada para aprobación administrativa.\n\nEl administrador revisará los datos técnicos antes de la activación final del cliente.');
                navigate('/technician/installations');
            } else {
                throw new Error(result.message || 'Error enviando instalación para aprobación');
            }

        } catch (error) {
            console.error('Error submitting completion:', error);
            setError(error.message || 'Error enviando instalación para aprobación');
        } finally {
            setSubmitting(false);
        }
    };

    const steps = [
        {
            label: 'Información General',
            description: 'Notas y tiempos de instalación'
        },
        {
            label: 'Pruebas Técnicas',
            description: 'Velocidad, ping y señal'
        },
        {
            label: 'Equipos y Satisfacción',
            description: 'Equipos instalados y calificación del cliente'
        },
        {
            label: 'Evidencia Fotográfica',
            description: 'Subir fotos del trabajo realizado'
        },
        {
            label: 'Revisión Final',
            description: 'Confirmar datos antes de enviar'
        }
    ];

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando detalles de instalación...</Typography>
            </Box>
        );
    }

    if (!installation) {
        return (
            <Alert severity="error">
                No se encontró la instalación o no tienes acceso a ella.
            </Alert>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
                {/* Header */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Completar Instalación
                    </Typography>
                    <Typography variant="body1" color="textSecondary" gutterBottom>
                        Cliente: {installation.first_name} {installation.last_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Plan: {installation.plan_name} - {installation.download_speed}/{installation.upload_speed} Mbps
                    </Typography>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        ℹ️ Esta instalación será enviada para aprobación administrativa antes de la activación final del cliente.
                    </Alert>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Stepper */}
                <Paper sx={{ p: 3 }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {/* Paso 1: Información General */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6">Información General</Typography>
                            </StepLabel>
                            <StepContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Notas de Instalación *"
                                            value={completionData.installation_notes}
                                            onChange={(e) => handleInputChange('installation_notes', e.target.value)}
                                            placeholder="Describa los detalles de la instalación, configuraciones realizadas, etc."
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Hora de Inicio (automática)"
                                            value={completionData.installation_start_time ? new Date(completionData.installation_start_time).toLocaleString('es-PE') : 'No registrada'}
                                            InputProps={{ readOnly: true }}
                                            helperText="Se registró automáticamente al iniciar la instalación"
                                            sx={{ '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 'bold' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Coordenadas de Inicio (automática)"
                                            value={completionData.start_coordinates || 'No registrada'}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                            helperText="Se registraron automáticamente al iniciar la instalación"
                                            sx={{ '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 'bold' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Hora de Finalización (automática)"
                                            value={completionData.installation_end_time ? new Date(completionData.installation_end_time).toLocaleString('es-PE') : 'Se registra al completar'}
                                            InputProps={{ readOnly: true }}
                                            helperText="Se registra automáticamente al completar la instalación"
                                            sx={{ '& .MuiInputBase-input': { color: 'success.main', fontWeight: 'bold' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Coordenadas Finales (automática)"
                                            value={completionData.final_coordinates || 'Capturando...'}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                            helperText="Se capturan automáticamente al completar la instalación"
                                            sx={{ '& .MuiInputBase-input': { color: 'success.main', fontWeight: 'bold' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={captureCurrentGPS}
                                            disabled={capturingGPS}
                                            startIcon={capturingGPS ? <CircularProgress size={20} /> : <MyLocation />}
                                            fullWidth
                                            size="large"
                                            sx={{ 
                                                py: 1.5, 
                                                fontSize: '1rem',
                                                fontWeight: 'bold',
                                                borderRadius: 2
                                            }}
                                        >
                                            {capturingGPS ? 'Capturando ubicación...' : '📍 Recapturar coordenadas finales'}
                                        </Button>
                                        {completionData.final_coordinates && (
                                            <Alert severity="success" sx={{ mt: 1.5 }}>
                                                ✅ Coordenadas finales capturadas: {completionData.final_coordinates}
                                            </Alert>
                                        )}
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(0)}
                                    >
                                        Continuar
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Paso 2: Pruebas Técnicas */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6">Pruebas Técnicas</Typography>
                            </StepLabel>
                            <StepContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Box display="flex" gap={1.5} flexWrap="wrap" mb={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                startIcon={probingSignal ? <CircularProgress size={18} color="inherit" /> : <WifiTethering />}
                                                onClick={handleProbeSignal}
                                                disabled={probingSignal}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                {probingSignal ? 'Sondeando...' : '📡 Sondear Señal en Vivo (1-Clic)'}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                startIcon={runningSpeedTest ? <CircularProgress size={18} color="inherit" /> : <Speed />}
                                                onClick={handleRunSpeedTest}
                                                disabled={runningSpeedTest}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                {runningSpeedTest ? 'Midiendo...' : '🚀 Ejecutar Test de Velocidad'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <InputLabel id="select-ap-label">Antena AP / Sectorial de Apuntamiento *</InputLabel>
                                            <Select
                                                labelId="select-ap-label"
                                                id="select-ap"
                                                value={completionData.access_point_id || ''}
                                                label="Antena AP / Sectorial de Apuntamiento *"
                                                onChange={(e) => handleInputChange('access_point_id', e.target.value)}
                                                disabled={loadingAPs}
                                            >
                                                <MenuItem value="">
                                                    <em>Seleccione un AP/Sectorial</em>
                                                </MenuItem>
                                                {accessPoints.map(ap => (
                                                    <MenuItem key={ap.id} value={ap.id}>
                                                        {ap.name} {ap.ssid ? `(${ap.ssid})` : ''} - Zona: {ap.zone_name || 'Sin zona'}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {loadingAPs && <CircularProgress size={20} sx={{ mt: 1 }} />}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Velocidad de Descarga (Mbps) *"
                                            value={completionData.test_results.download_speed}
                                            onChange={(e) => handleInputChange('test_results.download_speed', e.target.value)}
                                            required
                                            InputProps={{
                                                startAdornment: <Speed sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Velocidad de Subida (Mbps) *"
                                            value={completionData.test_results.upload_speed}
                                            onChange={(e) => handleInputChange('test_results.upload_speed', e.target.value)}
                                            required
                                            InputProps={{
                                                startAdornment: <Speed sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Ping (ms) *"
                                            value={completionData.test_results.ping}
                                            onChange={(e) => handleInputChange('test_results.ping', e.target.value)}
                                            required
                                            InputProps={{
                                                startAdornment: <Timer sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Intensidad de Señal (dBm)"
                                            value={completionData.signal_strength_dbm}
                                            onChange={(e) => handleInputChange('signal_strength_dbm', e.target.value)}
                                            placeholder="Ej: -65"
                                            InputProps={{
                                                startAdornment: <NetworkWifi sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(1)}
                                    >
                                        Continuar
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Paso 3: Equipos y Satisfacción */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6">Equipos y Satisfacción</Typography>
                            </StepLabel>
                            <StepContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                                            <Typography variant="h6">Equipos e Insumos Instalados</Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<FlashOn />}
                                                    onClick={() => handleLoadKit('fibra')}
                                                    size="small"
                                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    ⚡ Kit Fibra (FTTH)
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={<FlashOn />}
                                                    onClick={() => handleLoadKit('inalambrico')}
                                                    size="small"
                                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    ⚡ Kit Radioenlace
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Add />}
                                                    onClick={() => setEquipmentDialog(true)}
                                                    size="small"
                                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    Agregar Equipo
                                                </Button>
                                            </Stack>
                                        </Box>

                                        {completionData.equipment_installed.length > 0 ? (
                                            <List>
                                                {completionData.equipment_installed.map((equipment) => (
                                                    <ListItem key={equipment.id} divider>
                                                        <ListItemText
                                                            primary={equipment.item_name}
                                                            secondary={
                                                                <Box>
                                                                    <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 'bold' }}>
                                                                        Adquisición: {equipment.acquisition_type === 'sale' ? 'Venta' : (equipment.acquisition_type === 'none' ? 'Ninguno' : 'Alquiler/Garantía')} - Costo: S/ {parseFloat(equipment.price || 0).toFixed(2)}
                                                                    </Typography>
                                                                    {equipment.serial_number && <Typography variant="caption">Serie: {equipment.serial_number}</Typography>}
                                                                    {equipment.mac_address && <Typography variant="caption" display="block">MAC: {equipment.mac_address}</Typography>}
                                                                    {equipment.notes && <Typography variant="caption" display="block">Notas: {equipment.notes}</Typography>}
                                                                </Box>
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                onClick={() => removeEquipment(equipment.id)}
                                                                color="error"
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        ) : (
                                            <Alert severity="info">No se han agregado equipos</Alert>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom>
                                            Satisfacción del Cliente *
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Rating
                                                value={completionData.customer_satisfaction}
                                                onChange={(event, newValue) => handleInputChange('customer_satisfaction', newValue)}
                                                size="large"
                                            />
                                            <Typography variant="body2">
                                                {completionData.customer_satisfaction > 0 ? `${completionData.customer_satisfaction} de 5 estrellas` : 'Sin calificar'}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="h6" gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            ✍️ Firma Digital de Conformidad del Cliente *
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                                            Pídale al cliente que dibuje su firma con el dedo en el recuadro para validar la conformidad de la instalación.
                                        </Typography>
                                        
                                        <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#fcfcfc', border: '1px solid #ccc', borderRadius: 2, maxWidth: 500 }}>
                                            <canvas
                                                id="client-signature-canvas"
                                                width="480"
                                                height="200"
                                                style={{
                                                    width: '100%',
                                                    height: '200px',
                                                    display: 'block',
                                                    cursor: 'crosshair',
                                                    backgroundColor: '#ffffff',
                                                    touchAction: 'none'
                                                }}
                                            />
                                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                                                <Button size="small" variant="text" color="error" onClick={clearSignature}>
                                                    Borrar Firma
                                                </Button>
                                                {signatureSaved ? (
                                                    <Chip label="Firma Registrada ✔" color="success" size="small" />
                                                ) : (
                                                    <Button size="small" variant="contained" color="primary" onClick={saveSignature}>
                                                        Guardar Firma
                                                    </Button>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Problemas Encontrados"
                                            value={completionData.issues_encountered}
                                            onChange={(e) => handleInputChange('issues_encountered', e.target.value)}
                                            placeholder="Describa cualquier problema o dificultad durante la instalación"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Recomendaciones"
                                            value={completionData.recommendations}
                                            onChange={(e) => handleInputChange('recommendations', e.target.value)}
                                            placeholder="Recomendaciones para el cliente o mejoras futuras"
                                        />
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 2 }}>
                                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(2)}
                                    >
                                        Continuar
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Paso 4: Evidencia Fotográfica */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6">Evidencia Fotográfica</Typography>
                            </StepLabel>
                            <StepContent>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Suba fotografías del equipo, antena, cableado, antes y después de la instalación como evidencia técnica de su trabajo.
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: '#fcfcfc' }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Tipo de Evidencia *</InputLabel>
                                                    <Select
                                                        value={photoType}
                                                        label="Tipo de Evidencia *"
                                                        onChange={(e) => setPhotoType(e.target.value)}
                                                    >
                                                        <MenuItem value="equipment">Foto del Equipo / Antena</MenuItem>
                                                        <MenuItem value="before">Antes de la Instalación</MenuItem>
                                                        <MenuItem value="during">Durante la Instalación</MenuItem>
                                                        <MenuItem value="after">Instalación Finalizada</MenuItem>
                                                        <MenuItem value="problem">Reporte de Problema</MenuItem>
                                                        <MenuItem value="solution">Solución Aplicada</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={5}>
                                                <TextField
                                                    fullWidth
                                                    label="Descripción / Leyenda"
                                                    value={photoCaption}
                                                    onChange={(e) => setPhotoCaption(e.target.value)}
                                                    placeholder="Ej: Antena CPE instalada en techo"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <input
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    id="photo-upload-input"
                                                    type="file"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files[0]) {
                                                            setPhotoFile(e.target.files[0]);
                                                        }
                                                    }}
                                                />
                                                <label htmlFor="photo-upload-input">
                                                    <Button
                                                        variant="outlined"
                                                        component="span"
                                                        fullWidth
                                                        startIcon={<PhotoCamera />}
                                                        color={photoFile ? "success" : "primary"}
                                                    >
                                                        {photoFile ? photoFile.name.substring(0, 15) + '...' : 'Seleccionar Foto'}
                                                    </Button>
                                                </label>
                                            </Grid>
                                            <Grid item xs={12} display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handlePhotoUpload}
                                                    disabled={!photoFile || uploadingPhoto}
                                                    startIcon={uploadingPhoto ? <CircularProgress size={20} /> : <Add />}
                                                >
                                                    {uploadingPhoto ? 'Subiendo...' : 'Subir Foto'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Fotos Cargadas ({uploadedPhotos.length})
                                    </Typography>
                                    {uploadedPhotos.length === 0 ? (
                                        <Alert severity="info" sx={{ mb: 2 }}>No se han subido evidencias fotográficas aún.</Alert>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {uploadedPhotos.map((photo) => (
                                                <Grid item xs={12} sm={6} md={4} key={photo.id}>
                                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                                        <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#eee' }}>
                                                            <img
                                                                src={photo.photo_url}
                                                                alt={photo.caption || photo.photo_type}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </Box>
                                                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                                <Chip
                                                                    size="small"
                                                                    label={
                                                                        photo.photo_type === 'equipment' ? 'Equipo/Antena' :
                                                                        photo.photo_type === 'before' ? 'Antes' :
                                                                        photo.photo_type === 'during' ? 'Durante' :
                                                                        photo.photo_type === 'after' ? 'Después' :
                                                                        photo.photo_type === 'problem' ? 'Problema' :
                                                                        photo.photo_type === 'solution' ? 'Solución' : photo.photo_type
                                                                    }
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handlePhotoDelete(photo.id)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                            {photo.caption && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {photo.caption}
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!validateStep(3)}
                                    >
                                        Continuar
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>

                        {/* Paso 5: Revisión Final */}
                        <Step>
                            <StepLabel>
                                <Typography variant="h6">Revisión Final</Typography>
                            </StepLabel>
                            <StepContent>
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    ⚠️ Revise cuidadosamente todos los datos antes de enviar. La instalación será enviada para aprobación administrativa.
                                </Alert>

                                {/* Resumen de datos */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>Información del Cliente</Typography>
                                                <Typography>👤 {installation.first_name} {installation.last_name}</Typography>
                                                <Typography>📞 {installation.phone}</Typography>
                                                <Typography>📍 {installation.address}</Typography>
                                                <Typography>🌐 {installation.plan_name}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>Resultados de Pruebas</Typography>
                                                <Typography>📶 Descarga: {completionData.test_results.download_speed} Mbps</Typography>
                                                <Typography>📤 Subida: {completionData.test_results.upload_speed} Mbps</Typography>
                                                <Typography>⚡ Ping: {completionData.test_results.ping} ms</Typography>
                                                <Typography>⭐ Satisfacción: {completionData.customer_satisfaction}/5 estrellas</Typography>
                                                <Typography>📡 AP/Sectorial: {accessPoints.find(ap => ap.id === completionData.access_point_id)?.name || 'Ninguno'}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>Notas de Instalación</Typography>
                                                <Typography>{completionData.installation_notes}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {completionData.equipment_installed.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>Equipos Instalados ({completionData.equipment_installed.length})</Typography>
                                                    {completionData.equipment_installed.map((eq, index) => (
                                                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                                            🔧 {eq.item_name} - {eq.acquisition_type === 'sale' ? 'Venta' : (eq.acquisition_type === 'none' ? 'Ninguno' : 'Alquiler/Garantía')} (S/ {parseFloat(eq.price || 0).toFixed(2)})
                                                        </Typography>
                                                    ))}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {completionData.client_signature && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>Firma del Cliente</Typography>
                                                    <Box
                                                        component="img"
                                                        src={completionData.client_signature}
                                                        alt="Firma del cliente"
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: 300,
                                                            height: 'auto',
                                                            borderRadius: 1,
                                                            border: '1px solid #ddd',
                                                            backgroundColor: '#fff',
                                                            p: 0.5
                                                        }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}

                                    {uploadedPhotos.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>Evidencias Fotográficas ({uploadedPhotos.length})</Typography>
                                                    <Grid container spacing={1}>
                                                        {uploadedPhotos.map((photo) => (
                                                            <Grid item xs={6} sm={4} md={2} key={photo.id}>
                                                                <Box
                                                                    component="img"
                                                                    src={photo.photo_url}
                                                                    alt={photo.caption}
                                                                    sx={{
                                                                        width: '100%',
                                                                        height: 80,
                                                                        objectFit: 'cover',
                                                                        borderRadius: 1,
                                                                        border: '1px solid #ddd'
                                                                    }}
                                                                />
                                                                <Typography variant="caption" noWrap display="block" align="center">
                                                                    {photo.photo_type === 'equipment' ? 'Equipo' :
                                                                     photo.photo_type === 'before' ? 'Antes' :
                                                                     photo.photo_type === 'during' ? 'Durante' :
                                                                     photo.photo_type === 'after' ? 'Después' :
                                                                     photo.photo_type === 'problem' ? 'Problema' :
                                                                     photo.photo_type === 'solution' ? 'Solución' : photo.photo_type}
                                                                </Typography>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}
                                </Grid>

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setRejectDialog(true)}
                                        disabled={submitting}
                                        sx={{ mr: 'auto' }}
                                    >
                                        Rechazar Instalación
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setPostponeDialog(true)}
                                        disabled={submitting}
                                    >
                                        Posponer / Incidente
                                    </Button>
                                    <Button onClick={handleBack} sx={{ mr: 1 }}>
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={submitCompletion}
                                        disabled={submitting}
                                        startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
                                        size="large"
                                    >
                                        {submitting ? 'Enviando...' : 'Enviar para Aprobación'}
                                    </Button>
                                </Box>
                            </StepContent>
                        </Step>
                    </Stepper>
                </Paper>

                {/* Dialog para agregar equipo */}
                <Dialog open={equipmentDialog} onClose={() => setEquipmentDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Agregar Equipo Instalado</span>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<QrCodeScanner />}
                            onClick={() => {
                                setEquipmentDialog(false);
                                setScannerDialogOpen(true);
                            }}
                            size="small"
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                        >
                            📷 Escanear QR / Barras
                        </Button>
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {!newEquipment.item_name && (
                                <Grid item xs={12}>
                                    <Alert severity="info">Por favor seleccione un equipo de su inventario para comenzar.</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Seleccionar Equipo *"
                                    value={newEquipment.item_id || ''}
                                    onChange={(e) => {
                                        const itemId = e.target.value;
                                        const selectedItem = assignedInventory.find(item => item.item_id === itemId);
                                        if (selectedItem) {
                                            const series = serializedInventory.filter(ser => ser.item_id === itemId);
                                            const isSerialized = series.length > 0;
                                            
                                            // Pre-cargar valores por defecto basados en pre-acuerdo comercial
                                            const isRouter = selectedItem.item_name.toLowerCase().includes('router');
                                            const defaultAcqType = isRouter 
                                                ? (installation.router_acquisition_type || 'rental')
                                                : (installation.antenna_acquisition_type || 'rental');
                                            const defaultPrice = isRouter
                                                ? (installation.router_price || '0.00')
                                                : (installation.antenna_price || '0.00');
                                            
                                            setNewEquipment(prev => ({
                                                ...prev,
                                                item_id: itemId,
                                                item_name: selectedItem.item_name,
                                                serial_number: '',
                                                mac_address: '',
                                                quantity: 1,
                                                is_serialized: isSerialized,
                                                acquisition_type: defaultAcqType,
                                                price: defaultPrice
                                            }));
                                            setAvailableSeries(series);
                                        }
                                    }}
                                >
                                    {assignedInventory
                                        .filter(item => {
                                            const series = serializedInventory.filter(ser => ser.item_id === item.item_id);
                                            const isSerialized = series.length > 0;
                                            if (isSerialized) {
                                                const unusedSeries = series.filter(s => 
                                                    !completionData.equipment_installed.some(eq => eq.serial_number === s.serial_number)
                                                );
                                                return unusedSeries.length > 0;
                                            } else {
                                                return !completionData.equipment_installed.some(eq => eq.item_id === item.item_id);
                                            }
                                        })
                                        .map((item) => (
                                            <MenuItem key={item.item_id} value={item.item_id}>
                                                {item.item_name} {item.brand ? `(${item.brand} ${item.model})` : ''} - Stock: {item.quantity}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>

                            {newEquipment.is_serialized && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Seleccionar Número de Serie / MAC *"
                                        value={newEquipment.serial_number || ''}
                                        onChange={(e) => {
                                            const sn = e.target.value;
                                            const selectedSer = availableSeries.find(ser => ser.serial_number === sn);
                                            if (selectedSer) {
                                                setNewEquipment(prev => ({
                                                    ...prev,
                                                    serial_number: sn,
                                                    mac_address: selectedSer.mac_address || ''
                                                }));
                                            }
                                        }}
                                        helperText="Elija el número de serie físico del equipo que instaló"
                                    >
                                        {availableSeries
                                            .filter(ser => !completionData.equipment_installed.some(eq => eq.serial_number === ser.serial_number))
                                            .map((ser) => (
                                                <MenuItem key={ser.id} value={ser.serial_number}>
                                                    S/N: {ser.serial_number} {ser.mac_address ? `| MAC: ${ser.mac_address}` : ''} ({ser.condition === 'new' ? 'Nuevo' : (ser.condition === 'used' ? 'Usado' : ser.condition)})
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </Grid>
                            )}

                            {!newEquipment.is_serialized && newEquipment.item_id && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Cantidad Usada *"
                                        value={newEquipment.quantity}
                                        onChange={(e) => {
                                            const qty = Math.max(1, parseInt(e.target.value) || 1);
                                            const selectedItem = assignedInventory.find(item => item.item_id === newEquipment.item_id);
                                            const maxStock = selectedItem ? selectedItem.quantity : 9999;
                                            
                                            setNewEquipment(prev => ({
                                                ...prev,
                                                quantity: Math.min(qty, maxStock)
                                            }));
                                        }}
                                        inputProps={{ min: 1 }}
                                        helperText={`Ingrese la cantidad que utilizó. Máximo disponible en stock: ${assignedInventory.find(item => item.item_id === newEquipment.item_id)?.quantity || 0}`}
                                    />
                                </Grid>
                            )}

                            {newEquipment.is_serialized && newEquipment.serial_number && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Número de Serie"
                                            value={newEquipment.serial_number}
                                            InputProps={{ readOnly: true }}
                                            variant="filled"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Dirección MAC"
                                            value={newEquipment.mac_address}
                                            InputProps={{ readOnly: true }}
                                            variant="filled"
                                        />
                                    </Grid>
                                </>
                            )}
                            {newEquipment.item_id && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Modo de Adquisición"
                                            value={newEquipment.acquisition_type || 'rental'}
                                            onChange={(e) => setNewEquipment(prev => ({ ...prev, acquisition_type: e.target.value }))}
                                        >
                                            <MenuItem value="rental">Alquiler / Garantía</MenuItem>
                                            <MenuItem value="sale">Venta</MenuItem>
                                            <MenuItem value="none">Ninguno</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Precio Acordado (S/)"
                                            type="number"
                                            value={newEquipment.price || '0.00'}
                                            onChange={(e) => setNewEquipment(prev => ({ ...prev, price: e.target.value }))}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            disabled={newEquipment.acquisition_type === 'none'}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Notas"
                                    value={newEquipment.notes}
                                    onChange={(e) => setNewEquipment(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Observaciones adicionales sobre el equipo"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEquipmentDialog(false)}>Cancelar</Button>
                        <Button onClick={addEquipment} variant="contained">Agregar</Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog para rechazar instalación */}
                <Dialog open={rejectDialog} onClose={() => !rejecting && setRejectDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ color: 'error.main' }}>Rechazar Instalación</DialogTitle>
                    <DialogContent>
                        <Typography gutterBottom>
                            ¿Está seguro que desea rechazar y cancelar esta instalación? Esta acción notificará al administrador y cambiará el estado de la instalación.
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Motivo del Rechazo *"
                            fullWidth
                            multiline
                            rows={3}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Especifique por qué no se pudo completar la instalación (ej. cliente no estaba, no hay línea de vista, etc.)"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRejectDialog(false)} disabled={rejecting}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleRejectInstallation} 
                            color="error" 
                            variant="contained"
                            disabled={!rejectionReason.trim() || rejecting}
                            startIcon={rejecting && <CircularProgress size={16} color="inherit" />}
                        >
                            {rejecting ? 'Rechazando...' : 'Confirmar Rechazo'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog para posponer instalación / incidente */}
                <Dialog open={postponeDialog} onClose={() => !postponing && setPostponeDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ color: 'warning.main' }}>Posponer Instalación (Incidente)</DialogTitle>
                    <DialogContent>
                        <Typography gutterBottom sx={{ mt: 1 }}>
                            ¿Desea reportar un incidente y posponer esta instalación? El estado regresará a "Pendiente" y la nota técnica quedará registrada para reprogramarse.
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Motivo del Incidente / Posposición *"
                            fullWidth
                            multiline
                            rows={3}
                            value={postponeReason}
                            onChange={(e) => setPostponeReason(e.target.value)}
                            placeholder="Especifique el motivo (ej. clima adverso, falta de materiales, cliente no se encuentra, etc.)"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPostponeDialog(false)} disabled={postponing}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handlePostponeInstallation} 
                            color="warning" 
                            variant="contained"
                            disabled={!postponeReason.trim() || postponing}
                            startIcon={postponing && <CircularProgress size={16} color="inherit" />}
                        >
                            {postponing ? 'Procesando...' : 'Confirmar Posponer'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog para captura GPS de alta precisión */}
                <Dialog open={gpsLoading} disableEscapeKeyDown>
                    <DialogTitle>📍 Obteniendo Ubicación GPS Precisa</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, minWidth: 280 }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography variant="body1" align="center" gutterBottom>
                            {gpsStatusText}
                        </Typography>
                        {gpsAccuracy !== null && (
                            <Typography variant="body2" color={gpsAccuracy <= 30 ? "success.main" : "warning.main"} fontWeight="bold">
                                Precisión actual: {gpsAccuracy.toFixed(1)}m (Meta: ≤ 30m)
                            </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 2 }}>
                            Por favor, espere a la intemperie o cerca de una ventana para una captura rápida.
                        </Typography>
                    </DialogContent>
                </Dialog>

                {/* Modal Escáner QR / Código de Barras */}
                <Dialog open={scannerDialogOpen} onClose={() => setScannerDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>📷 Escanear Código de Barras / QR</DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Apunte la cámara hacia el código de barras o QR de la caja de la Antena, ONU o Router.
                            </Typography>
                            <Box id="qr-reader-container" sx={{ width: '100%', minHeight: 250, border: '2px dashed #6366f1', borderRadius: 2 }} />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setScannerDialogOpen(false)} color="inherit">
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default InstallationComplete;